import { cn } from "@/lib/utils";

interface PageTitleProps {
  children: React.ReactNode;
  subtitle?: string;
  className?: string;
}

/**
 * PageTitle - Page-level H1 heading.
 *
 * Typography token: `text-page-title` (30px mobile / 36px desktop, 800, Plus Jakarta Sans).
 * Use once per page. Includes `mb-8` wrapper spacing.
 * Optional `subtitle` renders as subtitle text (18px, 300 weight) beneath the heading.
 */
export function PageTitle({ children, subtitle, className }: PageTitleProps) {
  return (
    <div className={cn("mb-8", className)}>
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-heading text-primary">
        {children}
      </h1>
      {subtitle && <p className="text-lg font-light text-foreground-muted mt-2">{subtitle}</p>}
    </div>
  );
}

interface SectionHeadingProps {
  children: React.ReactNode;
  centered?: boolean;
  className?: string;
  as?: "h1" | "h2" | "h3";
}

/**
 * SectionHeading - Section-level heading (H2 by default).
 *
 * Typography token: `text-section-heading` (24px mobile / 30px desktop, 800, Plus Jakarta Sans).
 * Centered by default for landing page sections. Pass `centered={false}` for
 * left-aligned app pages. Use the `as` prop to change the rendered HTML element
 * when the visual level differs from the semantic level.
 */
export function SectionHeading({
  children,
  centered = true,
  className,
  as: Tag = "h2",
}: SectionHeadingProps) {
  return (
    <Tag
      className={cn(
        "text-2xl md:text-3xl font-extrabold tracking-tight font-heading text-primary",
        centered && "text-center",
        className
      )}
    >
      {children}
    </Tag>
  );
}

interface SectionSubheadingProps {
  children: React.ReactNode;
  centered?: boolean;
  className?: string;
}

/**
 * SectionSubheading - Descriptive body text beneath a SectionHeading.
 *
 * Typography token: `text-subtitle` (18px, 300, Plus Jakarta Sans).
 * Light weight (300) creates sophisticated contrast against heavy headings.
 * Centered by default to pair with SectionHeading on landing pages.
 */
export function SectionSubheading({
  children,
  centered = true,
  className,
}: SectionSubheadingProps) {
  return (
    <p
      className={cn(
        "text-lg font-light text-foreground-muted mt-4 max-w-2xl",
        centered && "mx-auto text-center",
        className
      )}
    >
      {children}
    </p>
  );
}

interface SubsectionHeadingProps {
  children: React.ReactNode;
  className?: string;
  as?: "h3" | "h4" | "h5";
}

/**
 * SubsectionHeading - Sub-section heading (H4 by default).
 *
 * Typography token: `text-subsection` (18px, 600, Plus Jakarta Sans).
 * Use for H4-level headings within sections: sidebar headings, sub-sections
 * inside card bodies, or any heading below SectionHeading/CardHeading level.
 * Use the `as` prop to change the rendered HTML element when the visual level
 * differs from the semantic level.
 */
export function SubsectionHeading({ children, className, as: Tag = "h4" }: SubsectionHeadingProps) {
  return (
    <Tag className={cn("text-lg font-semibold font-heading text-primary", className)}>
      {children}
    </Tag>
  );
}

interface CardHeadingProps {
  children: React.ReactNode;
  className?: string;
  as?: "h3" | "h4" | "h5";
}

/**
 * CardHeading - Card-level heading (H3 by default).
 *
 * Typography token: `text-card-heading` (20px, 700, Plus Jakarta Sans).
 * Use for card titles, modal titles, and panel headers. Use the `as` prop to
 * change the rendered HTML element when the visual level differs from the
 * semantic level.
 */
export function CardHeading({ children, className, as: Tag = "h3" }: CardHeadingProps) {
  return (
    <Tag className={cn("text-xl font-bold font-heading text-primary", className)}>{children}</Tag>
  );
}
